import React from 'react';

import Paragraph from '@/components/Paragraph';
import InlineCode from '@/components/InlineCode';

import FlexDemo from './FlexDemo';
import GrowItem from './GrowItem';
import Output from './Output';

function FlexGrowMultiple() {
  const [flexGrowMap, setFlexGrowMap] = React.useState({
    a: 1,
    b: 1,
  });

  const incrementChild = React.useCallback((childId) => {
    setFlexGrowMap((currentMap) => ({
      ...currentMap,
      [childId]: currentMap[childId] + 1,
    }));
  }, []);
  const decrementChild = React.useCallback((childId) => {
    setFlexGrowMap((currentMap) => {
      const currentVal = currentMap[childId];

      // Don't allow negative numbers
      if (currentVal <= 0) {
        return currentMap;
      }

      return { ...currentMap, [childId]: currentVal - 1 };
    });
  }, []);

  const totalGrow = React.useMemo(() => {
    return Object.values(flexGrowMap).reduce(
      (acc, item) => acc + item,
      0
    );
  }, [flexGrowMap]);

  let summaryStatus;
  if (totalGrow === 0) {
    summaryStatus = 'no-grow';
  } else if (Object.values(flexGrowMap).some((val) => val === 0)) {
    summaryStatus = 'single-child';
  } else {
    summaryStatus = 'mixed-up';
  }

  return (
    <>
      <FlexDemo initialValues={{}} controls={[]}>
        {(state) => (
          <Output state={state}>
            {['a', 'b'].map((childId) => (
              <GrowItem
                key={childId}
                id={childId}
                flexGrow={flexGrowMap[childId]}
                totalGrow={totalGrow}
                handleIncrement={incrementChild}
                handleDecrement={decrementChild}
              />
            ))}
          </Output>
        )}
      </FlexDemo>
      {summaryStatus === 'no-grow' && (
        <Paragraph>
          With no <InlineCode>flex-grow</InlineCode>, both children
          shrink down to their minimum comfortable size, and the extra
          space isn't distributed.
        </Paragraph>
      )}
      {summaryStatus === 'single-child' && (
        <Paragraph>
          When a single child is given a positive{' '}
          <InlineCode>flex-grow</InlineCode> value, it gobbles up all
          of the extra space. In this case, it doesn't matter what the
          number is: 1 and 1000 have the same effect.
        </Paragraph>
      )}
      {summaryStatus === 'mixed-up' && (
        <Paragraph>
          The first child wants {flexGrowMap.a}{' '}
          {flexGrowMap.a === 1 ? 'unit' : 'units'} of extra space,
          while the second child wants {flexGrowMap.b}{' '}
          {flexGrowMap.b === 1 ? 'unit' : 'units'}. That means the
          total # of units is <strong>{totalGrow}</strong> (
          {flexGrowMap.a} + {flexGrowMap.b}). Each child gets a
          proportional share of that extra space.
        </Paragraph>
      )}
    </>
  );
}

export default FlexGrowMultiple;
